import { useEffect, useState } from 'react';
import { Check, ChevronDown, Copy, Link2 } from '@/components/ui/icons';
import { useWorkspaceId } from '@/lib/nav';
import { useDb } from '@/lib/store';
import { authApi, errorMessage } from '@/lib/auth';
import { ROLES, useCanManage } from '@/lib/roles';
import { Modal } from '@/components/ui/Modal';
import { Popover } from '@/components/ui/Popover';
import { OptionMenu } from '@/components/ui/OptionMenu';

type InviteRole = 'member' | 'guest' | 'admin';

/**
 * Invites someone by email as a member, guest or admin. The server issues a one-time link
 * (valid 14 days) that you share with them; there's no email delivery yet.
 */
export function InviteModal({ open, onClose, email: initialEmail = '', role: initialRole = 'member' }: { open: boolean; onClose: () => void; email?: string; role?: InviteRole }) {
  const ws = useWorkspaceId();
  const workspace = useDb((db) => db.workspaces[ws]);
  const canManage = useCanManage();
  const [email, setEmail] = useState(initialEmail);
  const [role, setRole] = useState<InviteRole>(initialRole);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<{ url: string; email: string; existing: boolean; role: InviteRole } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(initialEmail);
    setRole(initialRole);
  }, [open, initialEmail, initialRole]);

  const reset = () => {
    setEmail('');
    setError(null);
    setLink(null);
    setCopied(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!email.includes('@')) return;
    setBusy(true);
    setError(null);
    try {
      const r = await authApi.createInvite(ws, email.trim(), role);
      setLink({ url: r.url, email: email.trim(), existing: r.existingAccount, role });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Invite people to ${workspace?.name ?? 'this workspace'}`}
      footer={
        link ? (
          <>
            <button className="btn outline" onClick={reset}>
              Invite another
            </button>
            <button className="btn primary" onClick={close}>
              Done
            </button>
          </>
        ) : (
          <>
            <button className="btn outline" onClick={close}>
              Cancel
            </button>
            <button className="btn primary" onClick={submit} disabled={busy || !email.includes('@')}>
              Create invite link
            </button>
          </>
        )
      }
    >
      {link ? (
        <div className="invite-result">
          <p className="muted" style={{ fontSize: 'var(--fs-md)', marginBottom: 10 }}>
            Send this link to <strong>{link.email}</strong>
            {link.role === 'member' ? '' : ` (joining as ${link.role === 'guest' ? 'a guest' : 'an admin'})`}.{' '}
            {link.existing ? 'They already have an account and will join after signing in.' : 'They’ll choose a name and password when they open it.'} The link works once
            and expires in 14 days.
          </p>
          <div className="invite-link">
            <Link2 size={15} className="faint" />
            <input className="input bare" readOnly value={link.url} onFocus={(e) => e.target.select()} />
            <button className="btn sm outline" onClick={copy}>
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="form-field">
            <label>Email</label>
            <input
              className="input"
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </div>
          <div className="form-field">
            <label>Invite as</label>
            <Popover
              placement="bottom-start"
              content={(close) => (
                <OptionMenu
                  options={(['member', 'guest', ...(canManage ? ['admin' as const] : [])] as InviteRole[]).map((key) => ({ key, ...ROLES[key] }))}
                  value={role}
                  onPick={(key) => (setRole(key), close())}
                />
              )}
            >
              <button type="button" className="input invite-role">
                {(() => {
                  const Icon = ROLES[role].icon;
                  return <Icon size={15} className="muted" />;
                })()}
                <span className="grow">{ROLES[role].label}</span>
                <ChevronDown size={14} className="faint" />
              </button>
            </Popover>
            <p className="invite-role-hint">{ROLES[role].description}</p>
          </div>
          {error && <div className="auth-error">{error}</div>}
        </>
      )}
    </Modal>
  );
}
